import { useEffect, useState, type ReactNode } from 'react';
import {
  CONTACT_METHODS,
  HEARD_FROM,
  QUESTIONNAIRE_NOTE_MAX,
  QUESTIONNAIRE_OTHER_MAX,
  localize,
  type ContactMethod,
  type HeardFrom,
  type Locale,
  type QuestionnaireDefinition,
} from '@360face/domain';
import type { HubApi, KioskVisit } from '../api/client';
import type { Messages } from '../i18n/messages';
import { KInput, useKeyboardInput } from './Keyboard';
import { SignaturePad } from './SignaturePad';
import {
  formPages,
  setConfirmed,
  emptyForm,
  formatBirth,
  pageMissing,
  setAesthetic,
  setConditionsNone,
  toFormRequest,
  toggleCondition,
  type FormDraft,
  type FormPage,
} from './questionnaire-form';

const CONTACT_LABEL: Record<ContactMethod, keyof Messages> = {
  email: 'qfContactEmail',
  whatsapp: 'qfContactWhatsapp',
  telegram: 'qfContactTelegram',
  phone: 'qfContactPhone',
};
const HEARD_LABEL: Record<HeardFrom, keyof Messages> = {
  instagram: 'qfHeardInstagram',
  tiktok: 'qfHeardTiktok',
  google: 'qfHeardGoogle',
  ai_assistant: 'qfHeardAi',
  friend: 'qfHeardFriend',
  classpass: 'qfHeardClasspass',
  passing_by: 'qfHeardPassing',
  other: 'qfHeardOther',
};

/** Local date YYYY-MM-DD of the kiosk (Portugal), for the birth-date check. */
function kioskToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Lisbon' }).format(new Date());
}

/**
 * Client form on the kiosk (v2, owner 15.10.2026): about you · health · aesthetic · sign.
 * Everything lives in this component's state and disappears on submit, cancel or idle reset.
 */
export function ClientForm({
  visit,
  initialName,
  locale,
  api,
  locationId,
  m,
  onCancel,
  onDone,
  submit,
}: {
  visit: KioskVisit;
  initialName: string;
  locale: Locale;
  api: HubApi;
  locationId: string;
  m: Messages;
  onCancel: () => void;
  onDone: (v: KioskVisit) => void;
  /** The kiosk's SubmitButton (busy state + error text). */
  submit: (props: { label: string; run: () => Promise<void> }) => ReactNode;
}) {
  const [def, setDef] = useState<QuestionnaireDefinition>();
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState<FormDraft>(() => emptyForm(initialName));
  const [page, setPage] = useState<FormPage>('about');
  const [touched, setTouched] = useState(false);
  const today = kioskToday();
  useEffect(() => {
    let live = true;
    api
      .questionnaireDefinition(locationId)
      .then((d) => live && setDef(d))
      .catch(() => live && setLoadError(true));
    return () => {
      live = false;
    };
  }, [api, locationId]);
  const birthKb = useKeyboardInput('digits', form.birthDigits, (v) => setForm((f) => ({ ...f, birthDigits: v })), 8);

  if (loadError) {
    return (
      <div className="stack">
        <p className="notice" role="alert">
          {m.qfLoadError}
        </p>
        <button className="btn soft block" onClick={onCancel}>
          ← {m.back}
        </button>
      </div>
    );
  }
  if (!def) return <p className="lead">{m.loading}</p>;

  const missing = pageMissing(page, form, today);
  const bad = (f: string) => touched && missing.includes(f);
  const pages = formPages(def);
  const idx = pages.indexOf(page);
  const titles: Record<FormPage, string> = {
    about: m.qfStepAbout,
    health: m.qfStepHealth,
    aesthetic: m.qfStepAesthetic,
    sign: m.qfStepSign,
  };
  const choice = (pressed: boolean, label: ReactNode, onClick: () => void, key?: string) => (
    <button key={key} type="button" className="choice" aria-pressed={pressed} onClick={onClick}>
      {label}
    </button>
  );
  const next = () => {
    if (missing.length > 0) return setTouched(true);
    setTouched(false);
    setPage(pages[idx + 1]!);
  };

  return (
    <div className="stack qform">
      <p className="eyebrow">
        {localize(def.title, locale)} · {idx + 1}/{pages.length}
      </p>
      <h2 className="k-h2">{titles[page]}</h2>

      {page === 'about' && (
        <>
          <p className="lead">{localize(def.intro, locale)}</p>
          <label className="field">
            <span>{m.qfName}</span>
            <KInput
              mode="text"
              max={80}
              className={bad('fullName') ? 'input invalid' : 'input'}
              value={form.fullName}
              onValue={(v) => setForm({ ...form, fullName: v })}
            />
          </label>
          <label className="field">
            <span>{m.qfBirth}</span>
            <input
              className={bad('birthDate') ? 'input invalid' : 'input'}
              autoComplete="off"
              {...birthKb}
              placeholder={m.qfBirthHint}
              value={formatBirth(form.birthDigits)}
              onChange={(e) => setForm({ ...form, birthDigits: e.target.value.replace(/\D/g, '').slice(0, 8) })}
            />
          </label>
          <div className={bad('contact') ? 'q-item invalid' : 'q-item'}>
            <p className="q-text">{m.qfContact}</p>
            <div className="q-choices">
              {CONTACT_METHODS.map((c) =>
                choice(form.contact === c, m[CONTACT_LABEL[c]] as string, () => setForm({ ...form, contact: c }), c),
              )}
            </div>
          </div>
          <div className={bad('pressure') ? 'q-item invalid' : 'q-item'}>
            <p className="q-text">{m.qfPressure}</p>
            <div className="q-scale" role="group" aria-label={m.qfPressure}>
              {[1, 2, 3, 4, 5].map((n) =>
                choice(form.pressure === n, n, () => setForm({ ...form, pressure: n }), String(n)),
              )}
            </div>
            <p className="footnote">{m.qfPressureHint}</p>
          </div>
          <div className={bad('similarBefore') ? 'q-item invalid' : 'q-item'}>
            <p className="q-text">{m.qfSimilar}</p>
            <div className="q-yn">
              {choice(form.similarBefore === true, m.qfYes, () => setForm({ ...form, similarBefore: true }))}
              {choice(form.similarBefore === false, m.qfNo, () => setForm({ ...form, similarBefore: false }))}
            </div>
          </div>
          <div className={bad('heardFrom') ? 'q-item invalid' : 'q-item'}>
            <p className="q-text">{m.qfHeard}</p>
            <div className="q-choices">
              {HEARD_FROM.map((h) =>
                choice(form.heardFrom === h, m[HEARD_LABEL[h]] as string, () => setForm({ ...form, heardFrom: h }), h),
              )}
            </div>
          </div>
        </>
      )}

      {page === 'health' && def.simple && (
        <>
          <p className="lead">{localize(def.intro, locale)}</p>
          {def.conditionsTitle && <p className="q-text">{localize(def.conditionsTitle, locale)}</p>}
          <ul className="q-conditions">
            {def.conditions.map((c) => (
              <li key={c.id}>
                <span>{localize(c.text, locale)}</span>
              </li>
            ))}
          </ul>
          {def.allergies && (
            <>
              <p className="q-text">{localize(def.allergies.title, locale)}</p>
              <ul className="q-conditions">
                {def.allergies.items.map((c) => (
                  <li key={c.id}>
                    <span>{localize(c.text, locale)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {def.notes?.map((n, i) => (
            <p key={i} className="footnote">
              {localize(n, locale)}
            </p>
          ))}
          <button
            type="button"
            className={bad('conditions') ? 'check invalid' : 'check'}
            role="checkbox"
            aria-checked={form.conditionsNone === true}
            onClick={() => setForm(setConfirmed(form, form.conditionsNone !== true))}
          >
            <span className="box" aria-hidden>
              {form.conditionsNone === true ? '✓' : ''}
            </span>
            <span>{def.confirm ? localize(def.confirm, locale) : m.qfNoneApply}</span>
          </button>
        </>
      )}

      {page === 'health' && !def.simple && (
        <>
          <p className="lead">{m.qfHealthRead}</p>
          <ul className={form.conditionsNone === false ? 'q-conditions pick' : 'q-conditions'}>
            {def.conditions.map((c) => {
              const on = form.conditionIds.includes(c.id);
              return (
                <li key={c.id}>
                  {form.conditionsNone === false ? (
                    <button
                      type="button"
                      className="check"
                      role="checkbox"
                      aria-checked={on}
                      onClick={() => setForm(toggleCondition(form, c.id))}
                    >
                      <span className="box" aria-hidden>
                        {on ? '✓' : ''}
                      </span>
                      <span>{localize(c.text, locale)}</span>
                    </button>
                  ) : (
                    <span>{localize(c.text, locale)}</span>
                  )}
                </li>
              );
            })}
          </ul>
          <div className={bad('conditions') ? 'q-item invalid' : 'q-item'}>
            <div className="q-yn stacked">
              {choice(form.conditionsNone === true, m.qfNoneApply, () => setForm(setConditionsNone(form, true)))}
              {choice(form.conditionsNone === false, m.qfSomeApply, () => setForm(setConditionsNone(form, false)))}
            </div>
          </div>
          {form.conditionsNone === false && (
            <>
              <p className={bad('conditionIds') ? 'err' : 'footnote'}>{m.qfTickWhich}</p>
              <KInput
                multiline
                mode="text"
                rows={2}
                max={QUESTIONNAIRE_NOTE_MAX}
                className="input q-detail"
                placeholder={m.qfNote}
                aria-label={m.qfNote}
                value={form.conditionNote}
                onValue={(v) => setForm({ ...form, conditionNote: v })}
              />
            </>
          )}
        </>
      )}

      {page === 'aesthetic' && (
        <>
          <div className={bad('aesthetic') || bad('aestheticDetail') ? 'q-item invalid' : 'q-item'}>
            <p className="q-text">{localize(def.aesthetic.question, locale)}</p>
            <div className="q-yn">
              {choice(form.aestheticHad === false, m.qfNo, () => setForm(setAesthetic(form, false)))}
              {choice(form.aestheticHad === true, m.qfYes, () => setForm(setAesthetic(form, true)))}
            </div>
            {form.aestheticHad && (
              <KInput
                multiline
                mode="text"
                rows={2}
                max={QUESTIONNAIRE_NOTE_MAX}
                className="input q-detail"
                placeholder={localize(def.aesthetic.hint, locale)}
                aria-label={m.qfAestheticWhat}
                value={form.aestheticDetail}
                onValue={(v) => setForm({ ...form, aestheticDetail: v })}
              />
            )}
          </div>
          <label className="field">
            <span>{localize(def.other, locale)}</span>
            <KInput
              multiline
              mode="text"
              rows={2}
              max={QUESTIONNAIRE_OTHER_MAX}
              className="input q-detail"
              value={form.otherNote}
              onValue={(v) => setForm({ ...form, otherNote: v })}
            />
          </label>
        </>
      )}

      {page === 'sign' && (
        <>
          <button
            type="button"
            className={bad('consent') ? 'check invalid' : 'check'}
            role="checkbox"
            aria-checked={form.consent}
            onClick={() => setForm({ ...form, consent: !form.consent })}
          >
            <span className="box" aria-hidden>
              {form.consent ? '✓' : ''}
            </span>
            <span>{localize(def.consent, locale)}</span>
          </button>
          <div className={bad('signature') ? 'q-item invalid' : 'q-item'}>
            <SignaturePad
              label={m.qfSignHere}
              clearLabel={m.qfClear}
              onChange={(signature) => setForm((f) => ({ ...f, signature }))}
            />
          </div>
        </>
      )}

      {touched && missing.length > 0 && (
        <p className="err" role="alert">
          {m.qfPageMissing}
        </p>
      )}

      {page !== 'sign' ? (
        <button className="btn primary block" onClick={next}>
          {m.qfNext}
        </button>
      ) : missing.length === 0 ? (
        submit({
          label: m.qfSubmit,
          run: async () => {
            const v = await api.submitQuestionnaire(visit, toFormRequest(def, form, today));
            setForm(emptyForm());
            onDone(v);
          },
        })
      ) : (
        <button className="btn primary block" aria-disabled="true" onClick={() => setTouched(true)}>
          {m.qfSubmit}
        </button>
      )}
      <div className="q-nav">
        {idx > 0 ? (
          <button
            className="btn ghost"
            onClick={() => {
              setTouched(false);
              setPage(pages[idx - 1]!);
            }}
          >
            ← {m.qfPrev}
          </button>
        ) : (
          <span />
        )}
        <button
          className="btn ghost"
          onClick={() => {
            setForm(emptyForm());
            onCancel();
          }}
        >
          {m.qfCancel}
        </button>
      </div>
      <p className="footnote">
        {m.qfPrivate}
        {def.draft && ` ${m.qfDraft}`}
      </p>
    </div>
  );
}
